import React, { useState } from 'react';
import styles from '../AdminPanel.module.css';
import { MdSave, MdSettings, MdWarning } from 'react-icons/md';

const Settings: React.FC = () => {
  const [cacheSettings, setCacheSettings] = useState({
    enabled: true,
    duration: 30
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    announcementPopup: true
  });
  
  const [securitySettings, setSecuritySettings] = useState({
    loginAttempts: 5,
    sessionTimeout: 60,
    passwordPolicy: 'medium'
  });
  
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  
  const handleCacheSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setCacheSettings({
      ...cacheSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleNotificationSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    
    setNotificationSettings({
      ...notificationSettings,
      [name]: checked
    });
  };
  
  const handleSecuritySettingsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const inputValue = type === 'number' ? parseInt(value) : value;
    
    setSecuritySettings({
      ...securitySettings,
      [name]: inputValue
    });
  };
  
  const handleSaveSettings = async () => {
    setSaveStatus('saving');
    
    try {
      // Burada gerçek bir API çağrısı veya Firebase güncellemesi yapılacak
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSaveStatus('success');
      
      // 3 saniye sonra durumu sıfırla
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Ayarlar kaydedilirken hata:', error);
      setSaveStatus('error');
    }
  };
  
  return (
    <div>
      <h2>Sistem Ayarları</h2>
      
      <div className={styles.cardSection}>
        <h3>Önbellek Ayarları</h3>
        
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox"
              name="enabled"
              checked={cacheSettings.enabled}
              onChange={handleCacheSettingsChange}
            />
            Önbellek Aktif
          </label>
          <p className={styles.formHint}>Önbelleğe alma, uygulama performansını artırır</p>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Önbellek Süresi (dakika)</label>
          <input 
            type="number"
            className={styles.formInput}
            name="duration"
            value={cacheSettings.duration}
            onChange={handleCacheSettingsChange}
            min="5"
            max="240"
            disabled={!cacheSettings.enabled}
          />
        </div>
      </div>
      
      <div className={styles.cardSection}>
        <h3>Bildirim Ayarları</h3>
        
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox"
              name="emailNotifications"
              checked={notificationSettings.emailNotifications}
              onChange={handleNotificationSettingsChange}
            />
            E-posta Bildirimleri
          </label>
          <p className={styles.formHint}>Kullanıcılara e-posta bildirimleri gönder</p>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox"
              name="pushNotifications"
              checked={notificationSettings.pushNotifications}
              onChange={handleNotificationSettingsChange}
            />
            Push Bildirimleri
          </label>
          <p className={styles.formHint}>Mobil ve tarayıcı push bildirimleri gönder</p>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox"
              name="announcementPopup"
              checked={notificationSettings.announcementPopup}
              onChange={handleNotificationSettingsChange}
            />
            Duyuru Popup'ları
          </label>
          <p className={styles.formHint}>Yeni duyuruları popup olarak göster</p>
        </div>
      </div>
      
      <div className={styles.cardSection}>
        <h3>Güvenlik Ayarları</h3>
        
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Maksimum Giriş Denemesi</label>
          <input 
            type="number"
            className={styles.formInput}
            name="loginAttempts"
            value={securitySettings.loginAttempts}
            onChange={handleSecuritySettingsChange}
            min="3"
            max="10"
          />
          <p className={styles.formHint}>Bu sayıdan fazla başarısız giriş denemesinde hesap kilitlenir</p>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Oturum Zaman Aşımı (dakika)</label>
          <input 
            type="number"
            className={styles.formInput}
            name="sessionTimeout"
            value={securitySettings.sessionTimeout}
            onChange={handleSecuritySettingsChange}
            min="15"
            max="180"
          />
          <p className={styles.formHint}>Kullanıcı hareketsizliğinde oturum sonlandırma süresi</p>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Şifre Politikası</label>
          <select 
            className={styles.formSelect}
            name="passwordPolicy"
            value={securitySettings.passwordPolicy}
            onChange={handleSecuritySettingsChange}
          >
            <option value="low">Basit (min. 6 karakter)</option>
            <option value="medium">Orta (min. 8 karakter, en az 1 rakam)</option>
            <option value="high">Güçlü (min. 10 karakter, özel karakter ve rakam)</option>
          </select>
        </div>
      </div>
      
      <div className={styles.formActions}>
        <button 
          className={styles.submitButton}
          onClick={handleSaveSettings}
          disabled={saveStatus === 'saving'}
        >
          <MdSave className={styles.buttonIcon} />
          {saveStatus === 'saving' ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
        </button>
        
        {saveStatus === 'success' && (
          <div className={styles.success}>
            <p>Ayarlar başarıyla kaydedildi!</p>
          </div>
        )}
        
        {saveStatus === 'error' && (
          <div className={styles.error}>
            <p>Ayarlar kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.</p>
          </div>
        )}
      </div>
      
      <div className={styles.cardSection}>
        <div className={styles.infoBox}>
          <MdSettings className={styles.infoIcon} />
          <div>
            <h4>Ayarlar Hakkında</h4>
            <p>Bu ayarlar, uygulamanın genel davranışını ve güvenlik politikalarını belirler. 
              Değişiklikler, tüm kullanıcıları etkileyecektir.</p>
          </div>
        </div>
        
        <div className={styles.warningBox}>
          <MdWarning className={styles.warningIcon} />
          <div>
            <h4>Dikkat</h4>
            <p>Güvenlik ayarlarında yapılan değişiklikler, kullanıcı deneyimini etkileyebilir. 
              Çok katı güvenlik politikaları, kullanıcı şikayetlerine neden olabilir.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
